<template>
  <div class="landing-page">
    <section class="hero-section py-20">
      <div class="container mx-auto px-6">
        <div class="hero-copy">
            <p class="hero-kicker">品牌短剧生产工作台</p>
            <h1 class="hero-title">把需求、剧本、分镜到成片交付放进一条清晰的 AI 工作流</h1>
            <p class="text-xl mb-8 hero-subtitle">
              从创意概念、剧本生成、视觉资产到视频成片交付，用一套 AI 协同流程快速推进你的短剧项目。
            </p>

            <div class="flex space-x-4 hero-cta-group">
              <el-button type="primary" size="large" @click="handlePrimaryAction">{{ primaryActionLabel }}</el-button>
              <el-button size="large" @click="handleSecondaryAction">{{ secondaryActionLabel }}</el-button>
            </div>
        </div>
      </div>
    </section>

    <section class="stats-section py-12 bg-white">
      <div class="container mx-auto px-6">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div v-for="stat in stats" :key="stat.label" class="stat-item text-center">
            <div class="text-4xl font-bold text-blue-600 mb-2">{{ stat.value }}</div>
            <div class="text-gray-600">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <section class="features-section bg-gray-50 py-20">
      <div class="container mx-auto px-6">
        <h2 class="section-title text-center mb-16">六大核心能力</h2>
        <div class="grid md:grid-cols-3 gap-8">
          <div v-for="feature in features" :key="feature.title" class="feature-card card">
            <div class="feature-icon w-16 h-16 bg-blue-100 rounded-full flex-center mb-4">
              <el-icon size="32" class="text-blue-600">
                <component :is="feature.icon" />
              </el-icon>
            </div>
            <h3 class="text-xl font-semibold mb-3">{{ feature.title }}</h3>
            <p class="text-gray-600">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <section id="workflow-overview" class="workflow-section py-20">
      <div class="container mx-auto px-6">
        <h2 class="section-title text-center mb-16">七步完成一个短剧项目</h2>
        <div class="workflow-steps">
          <div v-for="(step, index) in workflowSteps" :key="step.title" class="step">
            <div class="step-number">{{ index + 1 }}</div>
            <div class="step-content card">
              <h3 class="text-lg font-semibold mb-2">{{ step.title }}</h3>
              <p class="text-gray-600">{{ step.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="pricing-section bg-gray-50 py-20">
      <div class="container mx-auto px-6">
        <h2 class="section-title text-center mb-16">交付方案</h2>
        <div class="grid md:grid-cols-3 gap-8">
          <div v-for="plan in pricingPlans" :key="plan.name" class="pricing-card card">
            <div class="text-center">
              <h3 class="text-2xl font-bold mb-2">{{ plan.name }}</h3>
              <div class="text-4xl font-bold text-blue-600 mb-4">{{ plan.price }}</div>
              <ul class="text-left space-y-3 mb-6">
                <li v-for="feature in plan.features" :key="feature" class="flex items-center">
                  <el-icon class="text-green-500 mr-2"><CircleCheckFilled /></el-icon>
                  {{ feature }}
                </li>
              </ul>
              <el-button type="primary" block @click="handlePrimaryAction">立即开始</el-button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <footer class="footer-section bg-gray-900 text-white py-12">
      <div class="container mx-auto px-6">
        <div class="grid md:grid-cols-4 gap-8">
          <div>
            <h4 class="text-lg font-semibold mb-2">视核云听</h4>
            <p class="text-gray-400 mb-2">videocore</p>
            <p class="text-gray-400">面向品牌、内容团队和客户协同的 AI 短剧生产平台。</p>
          </div>

          <div>
            <h4 class="text-lg font-semibold mb-4">快速入口</h4>
            <ul class="space-y-2">
              <li><RouterLink to="/" class="text-gray-400 hover:text-white">首页</RouterLink></li>
              <li><RouterLink to="/submit" class="text-gray-400 hover:text-white">提交需求</RouterLink></li>
              <li><RouterLink to="/dashboard" class="text-gray-400 hover:text-white">项目进度</RouterLink></li>
            </ul>
          </div>

          <div>
            <h4 class="text-lg font-semibold mb-4">平台能力</h4>
            <ul class="space-y-2 text-gray-400">
              <li>剧本生成</li>
              <li>分镜设计</li>
              <li>视频合成</li>
            </ul>
          </div>

          <div>
            <h4 class="text-lg font-semibold mb-4">关注我们</h4>
            <div class="flex space-x-4">
              <el-icon size="24" class="cursor-pointer hover:text-blue-400"><User /></el-icon>
              <el-icon size="24" class="cursor-pointer hover:text-red-400"><VideoCamera /></el-icon>
              <el-icon size="24" class="cursor-pointer hover:text-blue-500"><Share /></el-icon>
            </div>
          </div>
        </div>

        <div class="footer-filing border-t border-gray-800 mt-8 pt-8 text-gray-400">
          <div class="footer-filing__links">
            <a
              class="footer-filing__link footer-filing__link--police"
              href="https://beian.mps.gov.cn/#/query/webSearch"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="前往全国互联网安全管理服务平台网站备案查询"
            >
              <span class="footer-filing__badge">公</span>
              晋公网安备14010502990553号
            </a>
            <a
              class="footer-filing__link"
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="前往工业和信息化部 ICP 备案查询"
            >
              晋ICP备2026005692号-1
            </a>
            <span class="footer-filing__company">© 2026 山西融跃网络科技有限公司 版权所有</span>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import {
  Check,
  CircleCheckFilled,
  Clock,
  Document,
  Picture,
  Share,
  User,
  VideoCamera
} from '@element-plus/icons-vue'

import { useUserStore } from '@/stores/user'

const router = useRouter()
const userStore = useUserStore()

const primaryActionLabel = computed(() => (userStore.isAuthenticated ? '新建项目' : '立即开始'))
const secondaryActionLabel = computed(() => (userStore.isAuthenticated ? '查看项目进度' : '了解流程'))

const stats = [
  { value: '9+', label: 'AI 专业智能体' },
  { value: '7', label: '标准制作阶段' },
  { value: '24h', label: '持续协同推进' },
  { value: '100%', label: '项目资产沉淀' },
]

const features = [
  {
    icon: Document,
    title: '智能剧本生成',
    description: '基于 AI 大模型，根据您的需求描述自动生成专业级剧本，支持多轮迭代优化。',
  },
  {
    icon: Picture,
    title: '视觉资产创作',
    description: 'AI 驱动的角色设计、场景生成，打造统一视觉风格和高质量美术资源。',
  },
  {
    icon: VideoCamera,
    title: '视频合成制作',
    description: '自动化剪辑、特效、配音和配乐，一站式完成后期制作。',
  },
  {
    icon: Check,
    title: '合规审核保障',
    description: '内置内容安全检测和审查机制，降低上线前的内容风险。',
  },
  {
    icon: Clock,
    title: '高效交付流程',
    description: '智能体协同工作，人机审批节点配合，保证质量和推进效率。',
  },
  {
    icon: User,
    title: '专属客户服务',
    description: '从需求对接到最终交付，全程围绕项目结果进行协同和反馈。',
  },
]

const workflowSteps = [
  { title: '提交需求', description: '填写创意概念、目标受众、风格偏好等基础信息。' },
  { title: '方案确认', description: 'AI 生成详细方案和执行建议，确认后启动制作。' },
  { title: '剧本创作', description: 'AI 生成剧本初稿，审批通过后进入分镜阶段。' },
  { title: '分镜设计', description: '根据剧本生成详细分镜脚本，包含画面描述和对白。' },
  { title: '素材生成', description: 'AI 生成角色形象、背景场景等视觉素材。' },
  { title: '视频合成', description: '自动剪辑、配音、配乐和特效，生成完整视频。' },
  { title: '审核交付', description: '内部质检后提交客户终审，根据反馈调整直至满意。' },
]

const pricingPlans = [
  {
    name: '基础版',
    price: '¥2,999',
    features: [
      '时长 ≤ 3 分钟',
      '标准画质（1080P）',
      'AI 剧本生成',
      '基础角色设计',
      '标准化背景音乐',
      '5 个工作日交付',
      '2 次修改机会',
    ],
  },
  {
    name: '专业版',
    price: '¥5,999',
    features: [
      '时长 ≤ 10 分钟',
      '超清画质（4K）',
      '高级剧本定制',
      '精细角色设计',
      '原创音乐配乐',
      '3 个工作日交付',
      '5 次修改机会',
      '优先客服支持',
    ],
  },
  {
    name: '企业版',
    price: '定制方案',
    features: [
      '时长不限',
      '电影级画质',
      '专属编剧团队',
      '定制化视觉风格',
      '专业配音演员',
      '加急交付通道',
      '无限次修改',
      '专属项目经理',
    ],
  },
]

const handlePrimaryAction = () => {
  if (userStore.isAuthenticated) {
    router.push('/submit')
    return
  }

  router.push('/login')
}

const handleSecondaryAction = () => {
  if (userStore.isAuthenticated) {
    router.push('/dashboard')
    return
  }

  document.getElementById('workflow-overview')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<style scoped>
.landing-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(76, 110, 245, 0.18), transparent 28%),
    radial-gradient(circle at top right, rgba(244, 114, 182, 0.12), transparent 24%),
    linear-gradient(180deg, #edf3ff 0%, #f7f9ff 58%, #fff8fd 100%);
  color: #0f172a;
}

.container {
  max-width: 1200px;
}

.hero-copy {
  max-width: 760px;
}

.hero-kicker {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(53, 109, 255, 0.12);
  color: #356dff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.hero-title {
  margin: 20px 0 24px;
  font-size: clamp(2.8rem, 6vw, 4.5rem);
  line-height: 1.08;
  letter-spacing: -0.04em;
}

.hero-subtitle {
  color: #475569;
  line-height: 1.8;
  max-width: 680px;
}

.hero-cta-group {
  flex-wrap: wrap;
  justify-content: flex-start;
}

.stats-section {
  border-top: 1px solid rgba(148, 163, 184, 0.16);
  border-bottom: 1px solid rgba(148, 163, 184, 0.16);
}

.card {
  height: 100%;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 24px;
  padding: 28px;
  box-shadow: 0 18px 40px rgba(30, 41, 59, 0.08);
}

.flex-center {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.section-title {
  font-size: 36px;
  color: #0f172a;
}

.workflow-steps {
  display: grid;
  gap: 20px;
}

.step {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 18px;
  align-items: stretch;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 22px;
  background: linear-gradient(135deg, #356dff, #5b38f7);
  color: #fff;
  font-size: 24px;
  font-weight: 800;
  box-shadow: 0 14px 28px rgba(79, 92, 241, 0.28);
}

.step-content {
  min-height: 104px;
}

.footer-filing {
  display: flex;
  justify-content: center;
}

.footer-filing__links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 14px;
  font-size: 14px;
  line-height: 1.8;
}

.footer-filing__link,
.footer-filing__company {
  color: #9ca3af;
}

.footer-filing__link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  transition: color 0.2s ease;
}

.footer-filing__link:hover {
  color: #e5e7eb;
}

.footer-filing__badge {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: linear-gradient(180deg, #d7b766, #a8842f);
  color: #111827;
  font-size: 11px;
  font-weight: 800;
  flex: 0 0 auto;
}

.footer-filing__company {
  white-space: nowrap;
}

@media (max-width: 900px) {
  .section-title {
    font-size: 30px;
  }
}

@media (max-width: 768px) {
  .step {
    grid-template-columns: 1fr;
  }

  .step-number {
    min-height: 72px;
  }

  .footer-filing__links {
    gap: 8px 12px;
    font-size: 13px;
  }

  .footer-filing__company {
    white-space: normal;
    text-align: center;
  }
}
</style>
